import type { UomPreferences } from '../types';
import { api } from './api';

export interface UserPreferenceResponse {
  id: string;
  userId: string;
  themeSettings: {
    theme?: 'light' | 'dark';
    layoutType?: 'vertical' | 'horizontal';
    sidebarCollapsed?: boolean;
    sidebarWidth?: number;
    primaryColor?: string;
    borderRadius?: string;
    componentSize?: 'small' | 'middle' | 'large';
    fontFamily?: string;
    layoutDensity?: 'comfortable' | 'middle' | 'compact';
    layoutWidth?: 'fluid' | 'container';
  };
  tableSettings: {
    [tableId: string]: {
      columns: {
        [columnKey: string]: {
          dataIndex: string;
          visible: boolean;
          width?: number;
          order: number;
        };
      };
      pagination: {
        pageSize: number;
        current: number;
        showSizeChanger: boolean;
        showQuickJumper: boolean;
        pageSizeOptions: number[];
      };
      sort: {
        sortBy?: string;
        sortOrder?: 'ASC' | 'DESC' | 'ascend' | 'descend';
      };
      lastUpdated: number;
    };
  };
  additionalSettings: Record<string, any>;
  createdAt: string;
  updatedAt: string;
}

export interface UpdateUserPreferenceDto {
  themeSettings?: UserPreferenceResponse['themeSettings'];
  tableSettings?: UserPreferenceResponse['tableSettings'];
  additionalSettings?: Record<string, any>;
}

class UserPreferencesService {
  private baseUrl = '/user-preferences';

  /**
   * Get current user preferences
   */
  async getUserPreferences(): Promise<UserPreferenceResponse> {
    const response = await api.get<UserPreferenceResponse>(this.baseUrl);
    return response.data;
  }

  /**
   * Update user preferences
   */
  async updateUserPreferences(updateDto: UpdateUserPreferenceDto): Promise<UserPreferenceResponse> {
    const response = await api.put<UserPreferenceResponse>(this.baseUrl, updateDto);
    return response.data;
  }

  /**
   * Reset user preferences to defaults
   */
  async resetUserPreferences(): Promise<UserPreferenceResponse> {
    const response = await api.delete<UserPreferenceResponse>(this.baseUrl);
    return response.data;
  }

  /**
   * Update theme settings only
   */
  async updateThemeSettings(
    themeSettings: UserPreferenceResponse['themeSettings'],
  ): Promise<UserPreferenceResponse> {
    const response = await api.put<UserPreferenceResponse>(`${this.baseUrl}/theme`, themeSettings);
    return response.data;
  }

  /**
   * Update table settings for a specific table
   */
  async updateTableSettings(tableId: string, tableSettings: any): Promise<UserPreferenceResponse> {
    const response = await api.put<UserPreferenceResponse>(
      `${this.baseUrl}/tables/${tableId}`,
      tableSettings,
    );
    return response.data;
  }

  /**
   * Reset table settings for a specific table
   */
  async resetTableSettings(tableId: string): Promise<UserPreferenceResponse> {
    const response = await api.delete<UserPreferenceResponse>(`${this.baseUrl}/tables/${tableId}`);
    return response.data;
  }

  /**
   * Get UOM preferences from additional settings
   */
  async getUomPreferences(): Promise<UomPreferences | null> {
    try {
      const preferences = await this.getUserPreferences();
      return (preferences.additionalSettings?.uomPreferences as UomPreferences) || null;
    } catch (error) {
      console.error('Failed to get UOM preferences:', error);
      return null;
    }
  }

  /**
   * Update UOM preferences
   */
  async updateUomPreferences(
    uomPreferences: Partial<UomPreferences>,
  ): Promise<UserPreferenceResponse> {
    const currentPreferences = await this.getUserPreferences();
    const existingUomPrefs = (currentPreferences.additionalSettings?.uomPreferences ||
      {}) as UomPreferences;

    const updatedUomPrefs: UomPreferences = {
      ...existingUomPrefs,
      ...uomPreferences,
    };

    const response = await api.put<UserPreferenceResponse>(this.baseUrl, {
      additionalSettings: {
        ...currentPreferences.additionalSettings,
        uomPreferences: updatedUomPrefs,
      },
    });

    return response.data;
  }

  /**
   * Reset UOM preferences to defaults
   */
  async resetUomPreferences(): Promise<UserPreferenceResponse> {
    const currentPreferences = await this.getUserPreferences();

    const defaultUomPrefs: UomPreferences = {
      displayFormat: 'NAME_CODE',
      showDecimalPlaces: true,
      showConversionFactor: false,
    };

    const response = await api.put<UserPreferenceResponse>(this.baseUrl, {
      additionalSettings: {
        ...currentPreferences.additionalSettings,
        uomPreferences: defaultUomPrefs,
      },
    });

    return response.data;
  }
}

export const userPreferencesService = new UserPreferencesService();
